import dayjs from 'dayjs'

// Helpers over the `sessions/sessions` store data (blocks with nested sub_sessions).

// Room accent colors, assigned by room order (tokens can't be per-room, so this is the one palette).
const ROOM_COLORS = ['#f78b09', '#3b82c4', '#7a5af8', '#2f9e6e', '#d1495b', '#0f9fb5', '#b9539c', '#8a6d5d', '#e8a317', '#4f9a6b']

export function roomColor (rooms, roomId) {
  const index = rooms.findIndex(room => room.id === roomId)
  return index === -1 ? 'var(--brand)' : ROOM_COLORS[index % ROOM_COLORS.length]
}

export function roomName (rooms, roomId) {
  const room = rooms.find(r => r.id === roomId)
  return room ? room.name : ''
}

export function isLive (session, now) {
  return now.isSameOrAfter(dayjs(session.start)) && now.isBefore(dayjs(session.end))
}

export function progress (session, now) {
  const start = dayjs(session.start)
  const total = dayjs(session.end).diff(start)
  return total > 0 ? Math.min(1, Math.max(0, now.diff(start) / total)) : 0
}

export function isBreak (session) {
  return ['break', 'lunch'].includes(session.type)
}

export function flatten (blocks) {
  const all = []
  blocks.forEach(block => {
    all.push(block)
    ;(block.sub_sessions || []).forEach(sub => all.push(sub))
  })
  return all
}

export function sortedSpeakers (session) {
  return [...(session.speakers || [])].sort((a, b) => ((a.pivot && a.pivot.position) || 0) - ((b.pivot && b.pivot.position) || 0))
}

// break / lunch / party rows without a code or speakers are shown as quiet "social" rows
const SOCIAL_ICONS = { break: 'local_cafe', lunch: 'restaurant', party: 'local_bar', freetime: 'beach_access' }

export function socialIcon (session) {
  if (session.code || (session.speakers && session.speakers.length)) return null
  return session.icon || SOCIAL_ICONS[session.type] || null
}

export function canStar (session) {
  return !socialIcon(session) && !(session.sub_sessions && session.sub_sessions.length)
}

// rating opens when the talk starts
export function canRate (session, now) {
  return !!session.ratings_enabled && dayjs(session.start).isBefore(now)
}

export function canAsk (session, sessionsSettings) {
  return !!(sessionsSettings && sessionsSettings.showQuestionButton && session.questions_enabled)
}

export function myRating (session) {
  const ratings = session.ratings || []
  return ratings.length ? ratings[0].rating : 0
}

export function matches (session, query) {
  const q = query.toLowerCase()
  const contains = value => (value || '').toLowerCase().includes(q)
  return contains(session.name) ||
    (session.code || '').toLowerCase().startsWith(q) ||
    (session.speakers || []).some(s => contains(`${s.name} ${s.surname}`) || contains(s.subtitle))
}

export function escapeHtml (text) {
  return String(text || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
}

// escaped text with <mark> around the search query
export function highlight (text, query) {
  const safe = escapeHtml(text)
  if (!query) return safe
  const pattern = escapeHtml(query).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return safe.replace(new RegExp(`(${pattern})`, 'ig'), '<mark>$1</mark>')
}

export function speakerName (session) {
  const speaker = sortedSpeakers(session)[0]
  return speaker ? `${speaker.name} ${speaker.surname}` : ''
}

// "SESSION 2: Recent issues…" -> "SESSION 2"
export function shortBlockName (name) {
  return (name || '').replace(/:.*/, '')
}

export function timeRange (session) {
  return `${dayjs(session.start).format('H:mm')}–${dayjs(session.end).format('H:mm')}`
}

// Conference phase: before the event start (see eventStart), during, after the last session.
export function phase (blocks, now, start = eventStart(blocks)) {
  if (!blocks.length) return 'live'
  const last = blocks.reduce((max, s) => (dayjs(s.end).isAfter(max) ? dayjs(s.end) : max), dayjs(blocks[0].end))

  if (start && now.isBefore(start)) return 'pre'
  if (now.isAfter(last)) return 'post'
  return 'live'
}

// When the event starts (countdown target, start of the live phase):
// the admin setting (Settings → Maintenance → start) when it fits the programme, otherwise the first session
// of the first real programme day – a lone block the evening before (pre-registration) doesn't start the event.
// A setting left over from a previous year (more than two weeks before the programme or after it) is ignored.
export function eventStart (blocks, setting = null) {
  const configured = setting ? dayjs(setting) : null
  if (!blocks.length) return configured && configured.isValid() ? configured : null

  const firstDay = conferenceDays(blocks)[0]
  const fromProgramme = blocks
    .filter(s => dayjs(s.start).format('YYYY-MM-DD') === firstDay)
    .reduce((min, s) => (!min || dayjs(s.start).isBefore(min) ? dayjs(s.start) : min), null)

  if (configured && configured.isValid()) {
    const last = blocks.reduce((max, s) => (dayjs(s.end).isAfter(max) ? dayjs(s.end) : max), dayjs(blocks[0].end))
    if (!configured.isBefore(fromProgramme.subtract(14, 'day')) && configured.isBefore(last)) return configured
  }
  return fromProgramme
}

// Days with real programme; a day with a single block (e.g. registration the evening before) doesn't count.
export function conferenceDays (blocks) {
  const counts = {}
  blocks.forEach(s => {
    const day = dayjs(s.start).format('YYYY-MM-DD')
    counts[day] = (counts[day] || 0) + 1
  })
  const all = Object.keys(counts).sort()
  const real = all.filter(day => counts[day] > 1)
  return real.length ? real : all
}
